/**
 * The records page: rows (leads), with the full filter set and saved
 * segments, and a view of the same rows as the people behind them.
 *
 * "Rows", with a People menu item for the people view: the two menu items
 * are the switch (see recordsView.ts).
 * Both views take the same segment and filters: the people view sends the
 * leads list's own filter body to the people search, so nothing is dropped
 * between them. Each row shows its stream's row type (a lead, an order...).
 *
 * Segments are saved filter tabs, and they are the most-used write in the
 * whole product: 330 `filter_tabs.*` calls in 60 days, more than any genuine
 * configuration change. They are how you navigate 6.2M rows, so each one owns
 * a complete filter state and switching restores it. They sync per user
 * through the server, so the same segments appear on any device, and
 * interoperate with the Alpine app's tabs, which are the same rows.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Checkbox, Group, Loader,
  Modal, Pagination, Pill, Stack, Switch, Table, Text, TextInput, Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowBackUp, IconChartBar, IconCopy, IconDownload,
  IconDeviceFloppy, IconRefresh, IconSearch, IconX,
} from '@tabler/icons-react';
import { useDebouncedValue, useLocalStorage, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { handoffFrom, isOrHandoff } from '../layout/search/handoff';
import { exportLeads } from '../api/upload';
import {
  SHARED_PREFIX, segmentFilters, useDeleteSharedSegment, useFilterTabs, useLeads, useRedistribute,
  useSaveFilterTabs, useSegmentCounts, useSharedSegmentCounts, useSharedSegments, useUpdateSharedSegment,
  type FilterTab, type FilterTabs, type LeadFilters, type SharedSegment,
} from '../api/queries';
import { toFilters, toStoredState } from '../api/filterState';
import { TOKENS, formatDateTime, formatGBP, formatNumber } from '../theme';
import { SpeedChip } from '../ui';
import { LeadDetailModal } from './LeadDetailModal';
import { LeadsFilterPanel } from './LeadsFilterPanel';
import { LeadInsightsModal } from './LeadInsightsModal';
import { SegmentsRail, SegmentsSelect, type SegmentActions, type SharedSegmentActions } from './SegmentsRail';
import { ShareSegmentModal, type ShareResult, type ShareTarget } from './ShareSegmentModal';
import { useVocab } from '../vocab';
import { useCrmPermissions } from './crm/crmShared';
import { PeopleList } from './people/PeopleList';
import { ScoreBadge, useScoringInUse } from './scoring/scoringShared';
import { useRecordsView } from './recordsView';
import { RowTypeChip, useStreamOf } from './streams/streamShared';
import { hasAction, isScopedUser, seesLeadRows } from '../layout/nav';
import { ReturnDialog } from './returns/ReturnDialog';
import { useNavUser } from '../layout/navUser';
import { canDo } from '../layout/commands';
import { moneyViewFor } from './moneyView';
import { EnrolDialog, type EnrolSegmentChoice } from './workflows/EnrolDialog';
import {
  EMPTY_DRAFT, activeFilterCount, appliedFilters, cleanFilters, draftFilters, hasUnsavedChanges,
  hasPendingChanges, reduceDraft, type DraftAction, type DraftState,
} from './leadsFilterModel';

const PAGE_SIZE = 50;

/** Filters with a control on the always-visible row above the advanced panel. */
const TOP_ROW = new Set(['campaign_ids', 'supplier_ids', 'buyer_ids', 'date_range', 'date_from', 'date_to']);

export function LeadsPage() {
  const v = useVocab();
  // The "+N more" badge and the people view: never for a scoped
  // login, who is sent no count.
  const seesPeople = useCrmPermissions().seesPeople;
  // Someone given people but not leads sees only the people view.
  const navUser = useNavUser();
  const canLeads = seesLeadRows(navUser);
  // Enrolling a segment into a workflow: for those who may manage them.
  const canEnrol = canDo(navUser, 'manage_workflows') && seesPeople;
  const [enrolTarget, setEnrolTarget] = useState<EnrolSegmentChoice | null>(null);
  const view = useRecordsView(canLeads, seesPeople);
  const perPerson = view === 'people';
  const tabsQuery = useFilterTabs();
  const saveTabs = useSaveFilterTabs();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  /* Ids ticked for a bulk action, distinct from `selected`, which is the one
     lead whose detail modal is open. */
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<null | 'return' | 'redistribute'>(null);
  const [exporting, setExporting] = useState(false);
  const redistribute = useRedistribute();
  // Bulk return goes through the returns dialog: a reason, a note, and
  // optionally one buyer. Staff with manage_returns only.
  const canReturn = !isScopedUser(navUser) && hasAction(navUser, 'manage_returns');
  /* The panel edits a draft and Search applies it; both are overlays on the
     active tab's saved filters (see leadsFilterModel). */
  const [draftState, setDraftState] = useState<DraftState>(EMPTY_DRAFT);
  // Rename runs through a real input rather than window.prompt: prompt() is
  // easy to miss, cannot be styled, and is blocked outright in some contexts.
  // `id: null` is Save as segment, naming a segment that does not exist yet.
  // `sharedId` renames a shared segment; `copyOf` saves a shared segment's
  // own filters as a private copy, rather than what the panel shows.
  const [renaming, setRenaming] = useState<{
    id: number | null;
    name: string;
    sharedId?: string;
    copyOf?: SharedSegment;
  } | null>(null);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  /* A search with OR from the command bar. The filter panel cannot show OR,
     so while one is open it runs as handed over and the panel steps aside
     for a read-only banner, rather than showing filters that are not what
     the table is filtered by. */
  const [orSearch, setOrSearch] = useState<{ query: string; filters: LeadFilters } | null>(null);
  // Below about tablet width the rail would squeeze the table to nothing.
  const narrow = useMediaQuery('(max-width: 62em)') ?? false;
  const [railCollapsed, setRailCollapsed] = useLocalStorage({
    key: 'hawfinch_segments_rail_collapsed',
    defaultValue: false,
  });
  const [params, setParams] = useSearchParams();

  const stored = tabsQuery.data;
  const activeTab = stored?.tabs.find((t) => t.id === stored.active_tab_id) ?? stored?.tabs[0];
  const counts = useSegmentCounts(stored?.tabs, seesPeople ? 'people' : undefined);

  /* Shared segments. One that is open takes over from the private tab, and is
     remembered on this device only: which private tab is open is saved on the
     server with the tabs, but a shared segment is not one of them. */
  const sharedQuery = useSharedSegments();
  const sharedList = useMemo(() => sharedQuery.data?.segments ?? [], [sharedQuery.data]);
  const sharedCounts = useSharedSegmentCounts(sharedQuery.data?.segments);
  const updateShared = useUpdateSharedSegment();
  const deleteShared = useDeleteSharedSegment();
  const [activeSharedId, setActiveSharedId] = useLocalStorage<string | null>({
    key: 'hawfinch_active_shared_segment',
    defaultValue: null,
  });
  const activeShared = activeSharedId ? sharedList.find((s) => s.id === activeSharedId) : undefined;
  // Unshared, deleted or no longer shared with this person: back to the private tab.
  useEffect(() => {
    if (activeSharedId && sharedQuery.isSuccess && !sharedList.some((s) => s.id === activeSharedId)) {
      setActiveSharedId(null);
    }
  }, [activeSharedId, sharedQuery.isSuccess, sharedList, setActiveSharedId]);

  /* /leads?segment=<id>, from the Segments page: open that segment, then drop
     the param so a reload or a later switch is not pulled back to it. */
  const { mutate: saveTabsNow } = saveTabs;
  const wanted = params.get('segment');
  const sharedSettled = sharedQuery.isSuccess || sharedQuery.isError;
  useEffect(() => {
    if (wanted === null || !stored) return;
    if (wanted.startsWith(SHARED_PREFIX)) {
      // /leads?segment=shared:<id>: wait until the shared list says whether it exists.
      if (!sharedSettled) return;
      const sid = wanted.slice(SHARED_PREFIX.length);
      if (sharedList.some((s) => s.id === sid)) {
        setDraftState(EMPTY_DRAFT);
        setSearch('');
        setPage(1);
        setOrSearch(null);
        setActiveSharedId(sid);
      }
    } else {
      const id = Number(wanted);
      if (Number.isFinite(id) && stored.tabs.some((t) => t.id === id)) {
        setDraftState(EMPTY_DRAFT);
        setSearch('');
        setPage(1);
        setOrSearch(null);
        setActiveSharedId(null);
        if (stored.active_tab_id !== id) saveTabsNow({ ...stored, active_tab_id: id });
      }
    }
    setParams((p) => { p.delete('segment'); return p; }, { replace: true });
  }, [wanted, stored, saveTabsNow, setParams, sharedSettled, sharedList, setActiveSharedId]);

  /* A search handed over from the command bar ("Open in Leads", "Save as
     segment"): show exactly its filters, then clear the navigation state so
     a reload or Back does not apply it again. It travels in router state,
     not the URL, because the free text is often someone's name or email. */
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const handoff = handoffFrom(location.state);
    if (!handoff) return;
    if (isOrHandoff(handoff)) {
      setOrSearch({ query: handoff.query ?? '', filters: handoff.filters });
      setSearch('');
      setPage(1);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
      return;
    }
    setOrSearch(null);
    const applied = cleanFilters(handoff.filters);
    setDraftState({ applied, draft: null });
    setSearch(handoff.search);
    // Anything past the top row lives in the advanced filters, so show them
    // rather than leave a filter applied where nobody can see it.
    if (Object.keys(applied).some((k) => !TOP_ROW.has(k))) setPanelOpen(true);
    setPage(1);
    if (handoff.saveAs) setRenaming({ id: null, name: '' });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, navigate, location.pathname, location.search]);

  // The active tab's saved state is the baseline, so switching tabs restores
  // exactly what was saved. Saved state may be in either app's shape;
  // toFilters normalises it.
  const baseline = activeShared ? activeShared.filter_state : activeTab?.filter_state;
  const saved: LeadFilters = useMemo(() => toFilters(baseline), [baseline]);
  // What the table shows, and what export, insights and Save to tab use.
  const filters = appliedFilters(draftState, saved);
  // What the panel shows.
  const draft = draftFilters(draftState, saved);

  // Highest or lowest score first, from the Score column's header. Only
  // offered where the organisation scores; the newest first otherwise.
  const scoring = useScoringInUse();
  // Money this login may see (routes/money_view.rs). The supplier cost column
  // is optional and remembered per browser.
  const money = moneyViewFor(navUser);
  const [costColumn, setCostColumn] = useLocalStorage<boolean>({ key: 'rs-leads-cost-column', defaultValue: false });
  const showCost = money.cost && costColumn;
  const tableCols = 6 + (scoring ? 1 : 0) + (money.revenue ? 1 : 0) + (showCost ? 1 : 0);
  const [scoreSort, setScoreSort] = useState<'score_desc' | 'score_asc' | null>(null);
  const sortBy = scoring ? scoreSort : null;
  const query: LeadFilters = orSearch
    ? { ...orSearch.filters, page, page_size: PAGE_SIZE, ...(sortBy ? { sort: sortBy } : {}) }
    : {
      ...filters,
      page,
      page_size: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(sortBy ? { sort: sortBy } : {}),
    };
  const { data, isPending, isFetching, error } = useLeads(query, !perPerson);
  const cycleScoreSort = () => {
    setScoreSort((s) => (s === null ? 'score_desc' : s === 'score_desc' ? 'score_asc' : null));
    setPage(1);
  };
  // What the people view filters by: everything the lead table would use.
  const peopleFilter: LeadFilters = orSearch
    ? orSearch.filters
    : { ...filters, ...(debouncedSearch ? { search: debouncedSearch } : {}) };

  function dispatch(action: DraftAction) {
    setDraftState((s) => reduceDraft(s, action, saved));
    // Editing the draft leaves the table, and so its page, alone.
    if (action.type !== 'edit') setPage(1);
  }

  function patch(p: LeadFilters) {
    dispatch({ type: 'edit', patch: p });
  }

  function persist(next: FilterTabs) {
    saveTabs.mutate(next);
  }

  function switchTab(id: number) {
    // Guard: a non-numeric tab value would persist active_tab_id as NaN, which
    // JSON-serialises to null and orphans the selection.
    if (!stored || !Number.isFinite(id)) return;
    // The new tab's saved filters become both what the table shows and what
    // the panel edits.
    dispatch({ type: 'revert' });
    setSearch('');
    setOrSearch(null);
    setActiveSharedId(null);
    if (stored.active_tab_id !== id) persist({ ...stored, active_tab_id: id });
  }

  function switchShared(id: string) {
    dispatch({ type: 'revert' });
    setSearch('');
    setOrSearch(null);
    setActiveSharedId(id);
  }

  function saveCurrentTab() {
    if (activeShared) {
      if (activeShared.can_edit) {
        // Owner or admin: the shared segment itself changes, for everyone.
        updateShared.mutate({
          id: activeShared.id,
          body: { filter_state: toStoredState(cleanFilters(draft), activeShared.filter_state) },
        });
        dispatch({ type: 'search' });
      } else {
        // Anyone else keeps their edit as a private copy.
        setRenaming({ id: null, name: `${activeShared.name} (copy)` });
      }
      return;
    }
    if (!stored || !activeTab) return;
    persist({
      ...stored,
      tabs: stored.tabs.map((t) =>
        t.id === activeTab.id
          ? {
              ...t,
              // Writes both shapes, so this tab still opens in the Alpine app
              // and its column selection survives.
              filter_state: toStoredState(cleanFilters(draft), t.filter_state),
              has_changes: false,
            }
          : t,
      ),
    });
    // Saving stores what the panel shows and applies it, so an edit that was
    // never searched is saved and shown in one step rather than saved unseen.
    dispatch({ type: 'search' });
  }

  function newTab() {
    if (!stored) return;
    setActiveSharedId(null);
    const id = stored.next_tab_id;
    persist({
      tabs: [
        ...stored.tabs,
        { id, name: `Segment ${id}`, has_changes: false, filter_state: toStoredState(filters) },
      ],
      active_tab_id: id,
      next_tab_id: id + 1,
    });
    dispatch({ type: 'saved' });
  }

  /**
   * Save as segment: what the panel shows becomes a new segment and opens.
   * The segment it came from keeps what it had saved, so this is how to keep
   * an edit without overwriting the original.
   */
  function saveAsSegment(name: string, copyOf?: SharedSegment) {
    if (!stored) return;
    setActiveSharedId(null);
    const id = stored.next_tab_id;
    // A copy from a shared segment's menu takes what that segment saved;
    // otherwise the new segment is what the panel shows.
    const filterState = copyOf ? (copyOf.filter_state ?? null) : toStoredState(cleanFilters(draft));
    persist({
      tabs: [
        ...stored.tabs,
        { id, name, has_changes: false, filter_state: filterState },
      ],
      active_tab_id: id,
      next_tab_id: id + 1,
    });
    // The new segment's saved filters are exactly the draft, so there is
    // nothing left unsaved to carry.
    dispatch({ type: 'revert' });
  }

  function commitRename() {
    if (!stored || !renaming?.name.trim()) return;
    const name = renaming.name.trim();
    if (renaming.sharedId) {
      updateShared.mutate({ id: renaming.sharedId, body: { name } });
    } else if (renaming.id === null) {
      saveAsSegment(name, renaming.copyOf);
    } else {
      persist({
        ...stored,
        tabs: stored.tabs.map((t) => (t.id === renaming.id ? { ...t, name } : t)),
      });
    }
    setRenaming(null);
  }

  const segmentActions: SegmentActions = {
    onSelect: (id) => { if (activeShared || id !== activeTab?.id) switchTab(id); },
    onNew: newTab,
    onSaveAs: () => setRenaming({ id: null, name: '' }),
    onRename: (t: FilterTab) => setRenaming({ id: t.id, name: t.name }),
    onDelete: (t: FilterTab) => {
      if (window.confirm(`Delete the "${t.name}" segment?`)) deleteTab(t.id);
    },
  };

  const sharedActions: SharedSegmentActions = {
    onSelectShared: (id) => { if (id !== activeShared?.id) switchShared(id); },
    onShare: setShareTarget,
    ...(canEnrol ? {
      onEnrol: (t: ShareTarget) => setEnrolTarget(t.kind === 'private'
        ? { kind: 'private', id: String(t.tab.id), name: t.tab.name, filter: segmentFilters(t.tab) }
        : {
          kind: 'shared',
          id: t.segment.id,
          name: t.segment.name,
          filter: segmentFilters({ id: 0, name: t.segment.name, has_changes: false, filter_state: t.segment.filter_state } as FilterTab),
        }),
    } : {}),
    onSaveCopy: (s) => setRenaming({ id: null, name: `${s.name} (copy)`, copyOf: s }),
    onRenameShared: (s) => setRenaming({ id: null, name: s.name, sharedId: s.id }),
    onDeleteShared: (s) => {
      if (!window.confirm(`Delete the "${s.name}" segment for everyone it is shared with?`)) return;
      deleteShared.mutate(s.id, {
        onError: (e) => notifications.show({ color: 'red', title: 'Delete failed', message: e.message }),
      });
      if (s.id === activeShared?.id) {
        setActiveSharedId(null);
        dispatch({ type: 'revert' });
      }
    },
  };

  /** After sharing or unsharing, keep the same segment open where it now lives. */
  async function afterShare(target: ShareTarget | null, result: ShareResult) {
    if (result.kind === 'shared' && target?.kind === 'private' && target.tab.id === activeTab?.id && !activeShared) {
      setActiveSharedId(result.segment.id);
    } else if (result.kind === 'private' && target?.kind === 'shared' && target.segment.id === activeShared?.id) {
      setActiveSharedId(null);
      // The tabs changed on the server. Choose the returned one only once
      // they are read again, so a stale copy never overwrites them.
      const fresh = await tabsQuery.refetch();
      if (fresh.data?.tabs.some((t) => t.id === result.tabId)) {
        saveTabsNow({ ...fresh.data, active_tab_id: result.tabId });
      }
    }
  }

  function deleteTab(id: number) {
    if (!stored || stored.tabs.length <= 1) return;
    const remaining = stored.tabs.filter((t) => t.id !== id);
    persist({
      ...stored,
      tabs: remaining,
      active_tab_id: stored.active_tab_id === id ? (remaining[0]?.id ?? null) : stored.active_tab_id,
    });
    dispatch({ type: 'revert' });
  }

  const count = activeFilterCount(filters);
  const streamOf = useStreamOf();
  // The table shows something the tab has not saved.
  const dirty = hasUnsavedChanges(draftState, saved);
  // The panel holds edits that Search has not applied.
  const pending = hasPendingChanges(draftState, saved);
  const pageIds = useMemo(() => (data?.leads ?? []).map((l) => l.id), [data]);

  /* Export lives here rather than on a page of its own. The old app has a
     separate Export screen carrying a second copy of this filter card, which
     is how the two drift into exporting something other than what was on
     screen. The filters are already here; the button just uses them. */
  async function doExport() {
    setExporting(true);
    try {
      await exportLeads(filters);
      notifications.show({
        color: 'teal',
        title: 'Export downloaded',
        message: `${v.count(data?.pagination.total ?? 0, 'lead')}, matching the filters on screen.`,
      });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Export failed', message: (e as Error).message });
    }
    setExporting(false);
  }
  const checkedIds = [...checked];

  async function runBulk() {
    if (checkedIds.length === 0) return;
    try {
      if (bulk === 'redistribute') {
        const r = await redistribute.mutateAsync({ lead_ids: checkedIds });
        // Leads already being redistributed are skipped, not failed: say how
        // many in one line rather than listing each id as an error.
        const others = r.errors.filter((e) => !r.in_progress.some((id) => e.startsWith(`${id}:`)));
        const notes: string[] = [];
        if (r.in_progress.length) {
          notes.push(`${r.in_progress.length} skipped: already being redistributed.`);
        }
        if (others.length) notes.push(others.slice(0, 3).join('; '));
        notifications.show({
          color: r.queued > 0 ? 'teal' : 'orange',
          title: `${r.queued} of ${r.total} queued`,
          message: notes.length ? notes.join(' ') : 'The distribution worker runs next.',
        });
      }
      setChecked(new Set());
      setBulk(null);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Bulk action failed', message: (e as Error).message });
    }
  }

  const railProps = {
    tabs: stored?.tabs ?? [],
    activeId: activeTab?.id,
    dirty,
    counts: counts.data,
    shared: sharedList,
    sharedCounts: sharedCounts.totals,
    activeSharedId: activeShared?.id,
    canShare: sharedQuery.data?.can_share ?? false,
    ...segmentActions,
    ...sharedActions,
  };
  // A private tab, or a shared segment this person owns or administers.
  const canSaveHere = !activeShared || activeShared.can_edit;
  const naming = renaming === null ? null
    : renaming.sharedId || renaming.id !== null ? 'rename'
      : renaming.copyOf || (activeShared && !activeShared.can_edit) ? 'copy' : 'save-as';

  return (
    /* The rail sits beside everything else rather than above it, so the list
       keeps its full height. On a narrow screen it becomes a select. The
       negative margin pulls the filters up to a small gap under the top bar,
       since this page has no title to fill the shell's usual top padding. */
    <Group
      gap="sm"
      align="flex-start"
      wrap="nowrap"
      style={{ marginTop: 'calc(var(--mantine-spacing-xs) - var(--app-shell-padding, 0px))' }}
    >
      {stored && !narrow && (
        <SegmentsRail
          {...railProps}
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((c) => !c)}
        />
      )}

      {/* gap="sm": with no page title the default 16px band between the search
          row and the filter card is the last of the wasted vertical space. */}
      <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
        {/* No page title. The nav already says Rows, and the old app gives the
            row to the search box and saved filters instead: on a screen whose
            job is filtering, a heading is the least useful thing in the viewport. */}
        {/* Narrow: the segment picker gets its own row, so neither it nor the
            search box is squeezed to a few letters. */}
        {stored && narrow && <SegmentsSelect {...railProps} />}
        {/* Searching by name, email or phone lives in the top bar (⌘K). A
            search handed over from there still shows here, so the list is
            never narrowed by something nobody can see. */}
        {stored && search && (
          <Group gap="xs" align="center" wrap="nowrap">
            <Pill
              size="md"
              withRemoveButton
              onRemove={() => { setSearch(''); setPage(1); }}
              removeButtonProps={{ 'aria-label': 'Clear search' }}
            >
              <IconSearch size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
              Matching “{search}”
            </Pill>
          </Group>
        )}

        <Modal
          opened={renaming !== null}
          onClose={() => setRenaming(null)}
          title={naming === 'copy' ? 'Save a copy' : naming === 'save-as' ? 'Save as segment' : 'Rename segment'}
          size="sm"
        >
          <Stack>
            <TextInput
              data-autofocus
              label="Segment name"
              value={renaming?.name ?? ''}
              onChange={(e) => {
                const name = e.currentTarget.value;
                setRenaming((r) => (r ? { ...r, name } : r));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
              }}
            />
            {naming === 'copy' && (
              <Text size="xs" c="dimmed">
                {`A private copy in your own segments. "${renaming?.copyOf?.name ?? activeShared?.name ?? ''}" stays as it is for everyone it is shared with.`}
              </Text>
            )}
            {naming === 'save-as' && (
              <Text size="xs" c="dimmed">
                {`The filters on screen become a new segment. "${activeShared?.name ?? activeTab?.name ?? ''}" keeps what it had saved.`}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setRenaming(null)}>Cancel</Button>
              <Button onClick={commitRename} disabled={!renaming?.name.trim()}>Save</Button>
            </Group>
          </Stack>
        </Modal>

        <ShareSegmentModal
          target={shareTarget}
          onClose={() => setShareTarget(null)}
          onDone={(r) => { void afterShare(shareTarget, r); }}
        />

        {canEnrol && (
          <EnrolDialog
            opened={enrolTarget !== null}
            onClose={() => setEnrolTarget(null)}
            segment={enrolTarget}
            defaultRecordType={perPerson ? 'person' : 'submission'}
          />
        )}

        {/* Tighter than Mantine's 24px default. The old app fits two more lead
            rows above the fold on the same screen, and almost all of the
            difference was chrome around the filters. */}
        {orSearch && (
          <Card padding="sm" data-testid="or-search-banner">
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                  <IconSearch size={14} style={{ flexShrink: 0, color: TOKENS.textMuted }} />
                  <Text size="sm" fw={600} style={{ flexShrink: 0 }}>Search:</Text>
                  <Text size="sm" ff="monospace" truncate title={orSearch.query}>{orSearch.query || 'from the search bar'}</Text>
                </Group>
                <Text size="xs" c="dimmed">
                  This search uses OR, which the filters cannot show. Clear it to go back to the filters.
                </Text>
              </Stack>
              <Button
                size="compact-sm"
                variant="default"
                leftSection={<IconX size={13} />}
                onClick={() => { setOrSearch(null); setPage(1); }}
              >
                Clear
              </Button>
            </Group>
          </Card>
        )}

        <Card padding="sm" style={orSearch ? { display: 'none' } : undefined}>
          <LeadsFilterPanel
            leftActions={
              dirty && (
                <Group gap={2} wrap="nowrap">
                  <Tooltip
                    label={!canSaveHere
                      ? 'Save these filters as your own segment'
                      : activeShared ? 'Save these filters to the shared segment, for everyone' : 'Save these filters to the segment'}
                    withArrow
                  >
                    <ActionIcon
                      variant="light"
                      size={30}
                      onClick={saveCurrentTab}
                      loading={activeShared ? updateShared.isPending : saveTabs.isPending}
                      aria-label={!canSaveHere ? 'Save a copy' : activeShared ? 'Save to shared segment' : 'Save to segment'}
                    >
                      {canSaveHere ? <IconDeviceFloppy size={15} /> : <IconCopy size={15} />}
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Discard unsaved filter changes" withArrow>
                    <ActionIcon variant="subtle" size={30} onClick={() => dispatch({ type: 'revert' })} aria-label="Discard">
                      <IconX size={15} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )
            }
            activeCount={count}
            opened={panelOpen}
            filters={draft}
            pending={pending}
            onChange={patch}
            onSearch={() => dispatch({ type: 'search' })}
            onToggleAdvanced={() => setPanelOpen((o) => !o)}
            onReset={() => { dispatch({ type: 'reset' }); setSearch(''); }}
          />
        </Card>

        {perPerson && <PeopleList filter={peopleFilter} filtered={orSearch !== null || count > 0 || !!debouncedSearch} />}

        {!perPerson && error && <Alert color="red" variant="light">{error.message}</Alert>}

        {!perPerson && (
        <Card p={0}>
          {/* Counts, total value and page position, in the existing app's
              arrangement. The value figure opens the insights modal. */}
          <Group
            justify="space-between"
            px="md"
            py="xs"
            style={{ borderBottom: `1px solid ${TOKENS.border}` }}
          >
            <Group gap="md" align="flex-start">
              {/* The speed chip sits under the count it describes. */}
              <Stack gap={0}>
                <Group gap={6} align="baseline">
                  <Text fw={700}>{formatNumber(data?.pagination.total ?? 0)}</Text>
                  <Text size="xs" c="dimmed">{v.word(data?.pagination.total ?? 0, 'lead')}</Text>
                </Group>
                <SpeedChip stats={data?.query_stats} />
              </Stack>
              {!narrow && <Text c="dimmed">|</Text>}
              {money.revenue && (
                <Tooltip label="Break down where this value is coming from" withArrow position="bottom">
                  <UnstyledButton onClick={() => setInsightsOpen(true)}>
                    <Group gap={6} align="baseline">
                      <Text fw={700}>{formatGBP(data?.total_revenue ?? 0)}</Text>
                      <Group gap={3} align="center">
                        <Text size="xs" c="dimmed">{v.p.totalLeadValue}</Text>
                        <IconChartBar size={11} opacity={0.45} />
                      </Group>
                    </Group>
                  </UnstyledButton>
                </Tooltip>
              )}
              {money.cost && (
                <Switch
                  size="xs"
                  mt={3}
                  label={money.revenue ? 'Cost column' : 'Earned column'}
                  checked={costColumn}
                  onChange={(e) => setCostColumn(e.currentTarget.checked)}
                />
              )}
              {/* On a phone the pager below already says where you are, and the
                  separators only land at the ends of wrapped lines. */}
              {!narrow && (
                <>
                  <Text c="dimmed">|</Text>
                  <Text size="sm" c="dimmed">
                    Page {data?.pagination.page ?? 1} / {data?.pagination.total_pages ?? 1}
                  </Text>
                </>
              )}
            </Group>
            {isFetching && <Loader size="xs" />}
            <Button
              size="compact-xs"
              variant="default"
              leftSection={<IconDownload size={13} />}
              loading={exporting}
              onClick={doExport}
              // The export cannot carry OR yet, and must never export more
              // than the table shows.
              disabled={orSearch !== null}
              title={orSearch ? 'Searches with OR cannot be exported yet' : undefined}
            >
              Export
            </Button>
          </Group>

          {checked.size > 0 && (
            <Group
              justify="space-between"
              px="sm"
              py={6}
              style={{
                background: 'var(--mantine-color-default-hover)',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
            >
              <Text size="sm" fw={600}>{checked.size} selected</Text>
              <Group gap="xs">
                <Button
                  size="compact-xs"
                  variant="default"
                  leftSection={<IconRefresh size={13} />}
                  onClick={() => setBulk('redistribute')}
                >
                  Redistribute
                </Button>
                {canReturn && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    color="red"
                    leftSection={<IconArrowBackUp size={13} />}
                    onClick={() => setBulk('return')}
                  >
                    Return
                  </Button>
                )}
                <Button size="compact-xs" variant="subtle" onClick={() => setChecked(new Set())}>
                  Clear
                </Button>
              </Group>
            </Group>
          )}

          {isPending ? (
            <Group justify="center" p="xl"><Loader /></Group>
          ) : (
            <Table.ScrollContainer minWidth={820}>
              {/* Sizing copied from the Alpine table rather than approximated.
                  It sets `padding: 0.45rem 0.35rem` inline on its cells, 0.75rem
                  for primary text and 0.6875rem monospace at 35% opacity for the
                  secondary line, and gives its columns no widths at all — they
                  size to content. Forcing percentage widths is what spread this
                  across the screen no matter what the ratios were. */}
              <Table
                striped
                horizontalSpacing={6}
                verticalSpacing={7}
                /* Header size and treatment now come from the theme, matched to
                   the old app. Only nowrap is local: these headers must not wrap
                   when a column is squeezed. */
                styles={{ th: { whiteSpace: 'nowrap' } }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={34}>
                      <Checkbox
                        size="xs"
                        aria-label="Select all on this page"
                        checked={pageIds.length > 0 && pageIds.every((id) => checked.has(id))}
                        indeterminate={pageIds.some((id) => checked.has(id)) && !pageIds.every((id) => checked.has(id))}
                        onChange={(e) => {
                          const on = e.currentTarget.checked;
                          setChecked((prev) => {
                            const next = new Set(prev);
                            for (const id of pageIds) { if (on) next.add(id); else next.delete(id); }
                            return next;
                          });
                        }}
                      />
                    </Table.Th>
                    <Table.Th>{v.One('lead')}</Table.Th>
                    <Table.Th>{v.One('campaign')}</Table.Th>
                    <Table.Th>{v.p.contactDetails}</Table.Th>
                    <Table.Th>Delivery status</Table.Th>
                    <Table.Th>{v.p.sourceTag}</Table.Th>
                    {scoring && (
                      <Table.Th ta="right" w={70}>
                        <UnstyledButton
                          onClick={cycleScoreSort}
                          aria-label={sortBy === 'score_desc' ? 'Sorted by score, highest first' : sortBy === 'score_asc' ? 'Sorted by score, lowest first' : 'Sort by score'}
                          data-testid="score-sort"
                          style={{ font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', whiteSpace: 'nowrap' }}
                        >
                          {`Score${sortBy === 'score_desc' ? ' \u2193' : sortBy === 'score_asc' ? ' \u2191' : ''}`}
                        </UnstyledButton>
                      </Table.Th>
                    )}
                    {money.revenue && <Table.Th ta="right">{v.p.leadValue}</Table.Th>}
                    {showCost && (
                      <Table.Th ta="right">
                        <Tooltip label={`What the ${v.one('supplier')} is paid for this ${v.one('lead')}`} withArrow>
                          <span>{money.revenue ? 'Cost' : 'Earned'}</span>
                        </Tooltip>
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data?.leads.length === 0 && (
                    <Table.Tr><Table.Td colSpan={tableCols}>
                      <Text ta="center" c="dimmed" py="lg">{`No ${v.many('lead')} match these filters.`}</Text>
                    </Table.Td></Table.Tr>
                  )}
                  {data?.leads.map((lead) => (
                    <Table.Tr key={lead.id} onClick={() => setSelected(lead.id)} style={{ cursor: 'pointer' }}>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="xs"
                          aria-label={`Select ${lead.email || lead.id}`}
                          checked={checked.has(lead.id)}
                          onChange={(e) => {
                            const on = e.currentTarget.checked;
                            setChecked((prev) => {
                              const next = new Set(prev);
                              if (on) next.add(lead.id); else next.delete(lead.id);
                              return next;
                            });
                          }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TOKENS.text, lineHeight: 1.2 }}>
                          {lead.fullname || `${lead.firstname} ${lead.lastname}`.trim() || '(no name)'}
                          {seesPeople && lead.person_id && (lead.other_submissions ?? 0) > 0 && (
                            <Badge
                              component={Link}
                              to={`/people/${encodeURIComponent(lead.person_id)}`}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              size="xs"
                              variant="light"
                              color="gray"
                              radius="sm"
                              tt="none"
                              ml={6}
                              style={{ cursor: 'pointer', verticalAlign: 'text-bottom' }}
                              title={`${v.count(lead.other_submissions ?? 0, 'lead')} more from this ${v.one('person')}`}
                              data-testid="more-badge"
                            >
                              {`+${formatNumber(lead.other_submissions ?? 0)} more`}
                            </Badge>
                          )}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: TOKENS.textMuted, fontFamily: 'monospace', lineHeight: 1.1 }}>
                          ID: {lead.id.slice(0, 8)} · {formatDateTime(lead.created_at)}
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <span style={{ fontSize: '0.75rem', color: TOKENS.textSecondary, fontWeight: 500 }}>
                          {lead.campaign_name}
                        </span>
                        <RowTypeChip stream={streamOf(lead.campaign_id)} ml={6} />
                      </Table.Td>
                      <Table.Td>
                        <div style={{ fontSize: '0.75rem', color: TOKENS.text, fontWeight: 500, lineHeight: 1.2 }}>
                          {lead.email || '—'}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: TOKENS.textMuted, fontFamily: 'monospace', lineHeight: 1.1 }}>
                          {lead.phone1 || ''}
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={lead.sold ? 'chartGreen' : lead.delivery_status === 'rejected' ? 'chartRed' : 'gray'}
                        >
                          {lead.delivery_status || lead.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <span style={{ fontSize: '0.75rem', color: TOKENS.textSecondary, fontWeight: 500 }}>
                          {lead.source || '—'}
                        </span>
                      </Table.Td>
                      {scoring && (
                        <Table.Td ta="right">
                          <ScoreBadge score={lead.score} band={lead.score_band} size="xs" />
                        </Table.Td>
                      )}
                      {money.revenue && (
                        <Table.Td ta="right">
                          <span style={{ fontSize: '0.75rem', fontWeight: lead.revenue > 0 ? 700 : 400 }}>
                            {formatGBP(lead.revenue)}
                          </span>
                        </Table.Td>
                      )}
                      {showCost && (
                        <Table.Td ta="right">
                          <span style={{ fontSize: '0.75rem', color: TOKENS.textSecondary }}>
                            {formatGBP(lead.payout)}
                          </span>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>

        )}

        {!perPerson && data && data.pagination.total_pages > 1 && (
          <Group justify="center">
            <Pagination
              total={data.pagination.total_pages}
              value={page}
              onChange={setPage}
              disabled={isFetching}
            />
          </Group>
        )}

        {/* Both bulk actions confirm, and the confirm says what will happen
            rather than "are you sure". The old app gates its bulk return on
            `prompt(...) || 'default'`, so cancelling the prompt still returns
            every selected lead — which destroys revenue data now that a return
            reverses money. Cancel here cancels. */}
        <ReturnDialog
          target={{ kind: 'bulk', leadIds: checkedIds }}
          opened={bulk === 'return'}
          onClose={() => setBulk(null)}
          onDone={() => setChecked(new Set())}
        />

        <Modal
          opened={bulk === 'redistribute'}
          onClose={() => setBulk(null)}
          title={`Redistribute ${v.count(checked.size, 'lead')}?`}
        >
          <Stack>
            <Alert color="chartAmber" variant="light" title={`Some of these may already have ${v.p.soldPerfect}`}>
              <Text size="sm">
                {`Redistributing sends each ${v.one('lead')} through routing again, which may sell an already-${v.p.sold} ${v.one('lead')} a second time. The outcome is not known immediately.`}
              </Text>
            </Alert>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setBulk(null)}>Cancel</Button>
              <Button loading={redistribute.isPending} onClick={runBulk}>
                Redistribute
              </Button>
            </Group>
          </Stack>
        </Modal>

        <LeadDetailModal id={selected} onClose={() => setSelected(null)} />
        <LeadInsightsModal
          opened={insightsOpen}
          onClose={() => setInsightsOpen(false)}
          filters={orSearch ? orSearch.filters : { ...filters, ...(debouncedSearch ? { search: debouncedSearch } : {}) }}
        />
      </Stack>
    </Group>
  );
}
